import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { noOp } from '@neslotech/utils';

import { useAppDispatch, useAppSelector, useAuth } from '../hooks';

import { MandateActions } from '../actions/mandate/mandate.actions';
import { EventActions } from '../actions/event/event.actions';
import { EmployeeActions } from '../actions/employee/employee.actions';
import { LeaveRequestActions } from '../actions/leave-request/leave-request.actions';

import type { Mandate } from '../interfaces/client/mandate.interface';
import type { Event as BsmEvent } from '../interfaces/event/event.interface';
import type { Employee } from '../interfaces/employee/employee.interface';
import type { LeaveRequest } from '../interfaces/employee/leave-request.interface';

import Topbar from './components/Topbar';
import Icon from './components/Icon';
import KpiTile from './components/KpiTile';
import InlineStagePicker from './components/InlineStagePicker';
import InlineProgressEditor from './components/InlineProgressEditor';
import AvatarPile from './components/AvatarPile';
import MonthCalendar from './components/MonthCalendar';

import CreateMandateModal from './CreateMandateModal';

import {
  num,
  formatZar,
  hash,
  deriveStage,
  deriveCode,
  deriveId,
  initialsFromName
} from './lib/mandate-helpers';
import { useTableSort, SortableTh } from './lib/use-table-sort';
import FilterMenu, { applyFilters } from './components/FilterMenu';

// =============================================================================
//  Dashboard — v3 redesign
//
//  Data wiring:
//    Real     — current user (from auth), mandates list, events list, employees list.
//    Derived  — active-mandate count, pipeline-value sum.
//    Stubbed  — per-mandate progress + team, fees YTD, "closing this quarter"
//               list, and the activity feed.  These are flagged inline; the
//               backend doesn't expose those fields yet (Mandate has no
//               `progress`, `lead`, or `team`; there's no fees/activity API).
//               The stubs are derived deterministically from the mandate id
//               so the UI is stable between renders.
// =============================================================================

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];
const WEEKDAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEKDAY_SHORT = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

// =============================================================================
//  Greeting helpers
// =============================================================================

const greeting = (date: Date): string => {
  const h = date.getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};
const firstName = (full?: string): string => (full ? full.split(/\s+/)[0] : 'there');

const formatEyebrowDate = (date: Date): string => {
  const day = WEEKDAY_LONG[date.getDay()];
  const dom = date.getDate();
  const month = MONTHS[date.getMonth()];
  return `${day} · ${dom} ${month} ${date.getFullYear()}`;
};

// =============================================================================
//  Event helpers
// =============================================================================

// Group events into this week's window (today → today + 6) and sort.
interface UiEvent {
  date: Date;
  title: string;
  type: 'Internal' | 'Client';
  time: string;
  location: string;
  attendees?: number;
}

const toUiEvent = (e: BsmEvent): UiEvent => {
  const d = new Date(e.date);
  // Heuristic for type — events with a link are external/client-facing,
  // others are internal.  Cheap and good enough for the pilot.
  const type: UiEvent['type'] = e.link ? 'Client' : 'Internal';
  const time = Number.isNaN(d.getTime())
    ? '--:--'
    : `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return {
    date: d,
    title: e.name || 'Untitled event',
    type,
    time,
    location: type === 'Client' ? 'Teams' : 'BSM Boardroom', // TODO: needs `location` on event schema
    attendees: undefined // TODO: needs `attendees` on event schema
  };
};

// =============================================================================
//  Page
// =============================================================================

const DashboardV3 = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { profile, isAdmin } = useAuth();

  const mandates: Mandate[] = useAppSelector(({ mandate_store }) => mandate_store.mandates) || [];
  const events: BsmEvent[] = useAppSelector(({ event_store }) => event_store.events) || [];
  const employees: Employee[] =
    useAppSelector(({ employee_store }) => employee_store.employees) || [];
  const leaveRequests: LeaveRequest[] =
    useAppSelector(({ leave_request_store }) => leave_request_store.leaveRequests) || [];

  // Seed from the store so navigating back doesn't flash a loading state.
  const [loaded, setLoaded] = useState(mandates.length > 0);
  const [creating, setCreating] = useState(false);
  const [dealFilters, setDealFilters] = useState<Record<string, string>>({});
  // Deleting a mandate is a hard delete that also purges its documents, so it
  // confirms on the row rather than firing on a single click.
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | number | null>(null);
  const [deletingId, setDeletingId] = useState<string | number | null>(null);

  const handleDelete = (m: Mandate) => {
    if (!m.id || deletingId) return;
    setDeletingId(m.id);
    dispatch(
      MandateActions.deleteMandate(m.id, () => {
        setDeletingId(null);
        setConfirmDeleteId(null);
      })
    );
  };
  // Day picked in the calendar; drives the agenda panel below it.
  const [selectedDay, setSelectedDay] = useState<Date | undefined>(undefined);

  useEffect(() => {
    dispatch(MandateActions.loadMandates(() => setLoaded(true)));
    dispatch(EventActions.loadEvents(noOp));
    dispatch(EmployeeActions.loadEmployees(noOp));
    // Approved leave feeds the calendar's leave markers.
    dispatch(LeaveRequestActions.loadLeaveRequests(noOp));
  }, [dispatch]);

  // --- derived ------------------------------------------------------------

  const today = useMemo(() => new Date(), []);

  const activeMandates = useMemo(
    () => mandates.filter((m) => deriveStage(m) !== 'Closed'),
    [mandates]
  );

  const pipelineValueTotal = useMemo(
    () => activeMandates.reduce((sum, m) => sum + num(m.transaction_size), 0),
    [activeMandates]
  );

  // TODO: real fees-YTD aggregation lives on the back-end.  Until that
  // endpoint exists, sum the transaction_fee field across active mandates.
  const feesYtd = useMemo(
    () => activeMandates.reduce((sum, m) => sum + num(m.transaction_fee), 0),
    [activeMandates]
  );

  const closingMandates = useMemo(
    () => mandates.filter((m) => deriveStage(m) === 'Closing'),
    [mandates]
  );

  // Filters for the deal sheet, offered by the Filter button on that card.
  const DEAL_FILTER_FIELDS = useMemo(
    () => [
      { key: 'stage', label: 'Stage', accessor: (m: Mandate) => deriveStage(m) },
      { key: 'client', label: 'Client', accessor: (m: Mandate) => m.client },
      { key: 'sector', label: 'Sector', accessor: (m: Mandate) => m.sector }
    ],
    []
  );
  const dealMandates = useMemo(
    () => applyFilters(activeMandates, DEAL_FILTER_FIELDS, dealFilters),
    [activeMandates, DEAL_FILTER_FIELDS, dealFilters]
  );

  // The deal sheet is a top-6 preview; sort the filtered active set first so
  // the six shown reflect the chosen column, then slice.
  const SORT_ACCESSORS = useMemo(
    () => ({
      project: (m: Mandate) => deriveCode(m),
      client: (m: Mandate) => m.client,
      stage: (m: Mandate) => deriveStage(m),
      value: (m: Mandate) => num(m.transaction_size),
      progress: (m: Mandate) => (typeof m.progress === 'number' ? m.progress : null)
    }),
    []
  );
  const { sorted: sortedMandates, sort, toggle } = useTableSort(dealMandates, SORT_ACCESSORS);
  const dealSheetRows = useMemo(() => sortedMandates.slice(0, 6), [sortedMandates]);

  // Filter events to a 7-day window starting today.  If the server is
  // returning historical events the dashboard would otherwise show stale
  // entries.
  const weekEvents = useMemo(() => {
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const end = new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);
    return events
      .map(toUiEvent)
      .filter((e) => !Number.isNaN(e.date.getTime()) && e.date >= start && e.date < end)
      .sort((a, b) => a.date.getTime() - b.date.getTime())
      .slice(0, 6);
  }, [events, today]);

  // Agenda for the day picked in the calendar: events, birthdays and approved
  // leave, resolved to display labels.  Same three sources the markers use.
  const selectedDayItems = useMemo(() => {
    if (!selectedDay) return [];
    const sameDay = (d: Date) =>
      d.getFullYear() === selectedDay.getFullYear() &&
      d.getMonth() === selectedDay.getMonth() &&
      d.getDate() === selectedDay.getDate();

    const items: Array<{ kind: 'event' | 'birthday' | 'leave'; label: string; badge: string }> = [];

    events.forEach((e) => {
      if (!e.date) return;
      const d = new Date(e.date);
      if (!Number.isNaN(d.getTime()) && sameDay(d)) {
        items.push({ kind: 'event', label: e.name || 'Untitled event', badge: 'Event' });
      }
    });

    employees.forEach((emp) => {
      if (!emp.birthday) return;
      const b = new Date(emp.birthday);
      if (Number.isNaN(b.getTime())) return;
      if (b.getMonth() === selectedDay.getMonth() && b.getDate() === selectedDay.getDate()) {
        items.push({
          kind: 'birthday',
          label: `${emp.first_name} ${emp.last_name}`.trim(),
          badge: 'Birthday'
        });
      }
    });

    leaveRequests.forEach((leave) => {
      if (leave.status !== 'approved') return;
      const start = new Date(leave.start_date);
      const end = new Date(leave.end_date);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;
      const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      const dow = selectedDay.getDay();
      if (selectedDay >= startDay && selectedDay <= endDay && dow !== 0 && dow !== 6) {
        items.push({
          kind: 'leave',
          label: leave.employee || 'Employee on leave',
          badge: leave.category || 'Leave'
        });
      }
    });

    return items;
  }, [selectedDay, events, employees, leaveRequests]);

  // --- render -------------------------------------------------------------

  return (
    <>
      {creating && <CreateMandateModal onClose={() => setCreating(false)} />}
      <Topbar
        crumbs={['Workspace', 'Dashboard']}
        tools={
          // The Export-as-CSV/PDF flow for the Dashboard isn't wired yet.
          // Disabling + title makes the un-wired state obvious.
          <button className="btn ghost" type="button" disabled title="Coming soon">
            <Icon name="download" size={14} />
            Export
          </button>
        }
      />
      <div className="page">
        <div className="page-header">
          <div>
            <div className="eyebrow">{formatEyebrowDate(today)}</div>
            <h1 className="page-title">
              {greeting(today)}, {firstName(profile?.full_name)}.
            </h1>
            <div className="page-sub">
              {activeMandates.length} active mandate{activeMandates.length === 1 ? '' : 's'}
              {closingMandates.length > 0 && ` · ${closingMandates.length} closing this quarter`}
              {weekEvents.length > 0 &&
                ` · ${weekEvents.length} event${weekEvents.length === 1 ? '' : 's'} this week`}
            </div>
          </div>
          <div className="page-tools">
            <button className="btn" type="button" onClick={() => setCreating(true)}>
              <Icon name="plus" size={14} />
              New mandate
            </button>
            {/* Activity-feed isn't wired yet — disable until it is so the
                button doesn't no-op on click. */}
            <button className="btn primary" type="button" disabled title="Coming soon">
              <Icon name="upload" size={14} />
              Log activity
            </button>
          </div>
        </div>

        <div className="grid k4" style={{ marginBottom: 'var(--gap)' }}>
          <KpiTile
            label="Active mandates"
            value={String(activeMandates.length)}
            trend={mandates.length > 0 ? `of ${mandates.length} total` : undefined}
          />
          <KpiTile
            label="Pipeline value"
            value={formatZar(pipelineValueTotal)}
            mono
            trend="across all active deals"
          />
          <KpiTile
            label="Fees YTD"
            value={formatZar(feesYtd)}
            mono
            trend="sum of transaction fees"
          />
          <KpiTile
            label="Closing this quarter"
            value={String(closingMandates.length)}
            trend={
              closingMandates.length > 0
                ? closingMandates.slice(0, 3).map(deriveCode).join(' · ')
                : 'none scheduled'
            }
          />
        </div>

        <div className="grid dash">
          {/* --- Live deal sheet --- */}
          <div className="card flush">
            <div className="card-h">
              <h3>Live deal sheet</h3>
              <div className="actions">
                <FilterMenu<Mandate>
                  rows={activeMandates}
                  fields={DEAL_FILTER_FIELDS}
                  value={dealFilters}
                  onChange={setDealFilters}
                />
                <button className="btn ghost" type="button" onClick={() => navigate('/mandates')}>
                  View all
                  <Icon name="arrow" size={13} />
                </button>
              </div>
            </div>

            {activeMandates.length === 0 ? (
              <div className="muted" style={{ padding: 36, textAlign: 'center', fontSize: 13.5 }}>
                {/* Distinguish "still fetching" from "genuinely nothing" — the
                    two used to render the same message, so a cold load looked
                    like an empty deal sheet. */}
                {!loaded && mandates.length === 0 ? 'Loading…' : 'No active mandates yet.'}
              </div>
            ) : (
              <table className="tbl">
                <thead>
                  <tr>
                    <SortableTh label="Project" sortKey="project" sort={sort} onToggle={toggle} />
                    <SortableTh label="Client" sortKey="client" sort={sort} onToggle={toggle} />
                    <SortableTh label="Stage" sortKey="stage" sort={sort} onToggle={toggle} />
                    <SortableTh
                      label="Value"
                      sortKey="value"
                      sort={sort}
                      onToggle={toggle}
                      className="right"
                    />
                    <SortableTh label="Progress" sortKey="progress" sort={sort} onToggle={toggle} />
                    <th>Team</th>
                    {isAdmin && <th className="actions-cell" />}
                  </tr>
                </thead>
                <tbody>
                  {dealSheetRows.map((m) => {
                    // The backend has no mandate↔personnel join, so there is
                    // nothing real to show.  This previously rendered a
                    // hash-picked pile of colleagues' initials, which put real
                    // people on deals they had no part in.
                    const team: string[] = [];
                    return (
                      <tr
                        key={m.id}
                        onClick={() => navigate(`/mandates/${m.id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td className="strong">
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <span>{deriveCode(m)}</span>
                            <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                              {deriveId(m)}
                            </span>
                          </div>
                        </td>
                        <td>{m.client || '—'}</td>
                        <td>
                          <InlineStagePicker mandate={m} />
                        </td>
                        <td className="num right strong">{formatZar(m.transaction_size)}</td>
                        <td>
                          <InlineProgressEditor mandate={m} />
                        </td>
                        <td>
                          <AvatarPile team={team} />
                        </td>
                        {isAdmin && (
                          <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                            {confirmDeleteId === m.id ? (
                              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                <button
                                  className="btn ghost"
                                  type="button"
                                  disabled={deletingId === m.id}
                                  onClick={() => setConfirmDeleteId(null)}
                                >
                                  Cancel
                                </button>
                                <button
                                  className="btn danger"
                                  type="button"
                                  disabled={deletingId === m.id}
                                  onClick={() => handleDelete(m)}
                                  title="Permanently deletes this mandate and any documents attached to it"
                                >
                                  {deletingId === m.id ? 'Deleting…' : 'Confirm delete'}
                                </button>
                              </div>
                            ) : (
                              <button
                                className="btn ghost"
                                type="button"
                                onClick={() => m.id && setConfirmDeleteId(m.id)}
                                title="Delete this mandate"
                              >
                                Delete
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* --- Right column: calendar + This week --- */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap)' }}>
            <div className="card flush">
              <MonthCalendar
                events={events}
                employees={employees}
                leaveRequests={leaveRequests}
                selected={selectedDay}
                onSelectDay={setSelectedDay}
              />
            </div>

            {selectedDay && (
              <div className="card flush">
                <div className="card-h">
                  <h3>{formatEyebrowDate(selectedDay)}</h3>
                  <button
                    className="btn ghost"
                    type="button"
                    onClick={() => setSelectedDay(undefined)}
                  >
                    Clear
                  </button>
                </div>
                <div style={{ padding: '8px 0' }}>
                  {selectedDayItems.length === 0 ? (
                    <div
                      className="muted"
                      style={{ padding: '18px 22px', fontSize: 13, textAlign: 'center' }}
                    >
                      Nothing scheduled on this day.
                    </div>
                  ) : (
                    selectedDayItems.map((item, i) => (
                      <div
                        key={`${item.kind}-${i}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '9px 22px',
                          borderBottom:
                            i < selectedDayItems.length - 1 ? '1px solid var(--border)' : 'none'
                        }}
                      >
                        <i className={`dot ${item.kind}`} style={{ flex: '0 0 auto' }} />
                        <div style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: 'var(--ink)' }}>
                          {item.label}
                        </div>
                        <span className="chip muted">{item.badge}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            <div className="card flush">
              <div className="card-h">
                <h3>This week</h3>
                <span className="muted" style={{ fontSize: 12 }}>
                  {weekEvents.length} event{weekEvents.length === 1 ? '' : 's'}
                </span>
              </div>
              <div style={{ padding: '8px 0' }}>
                {weekEvents.length === 0 ? (
                  <div
                    className="muted"
                    style={{ padding: '20px 22px', fontSize: 13, textAlign: 'center' }}
                  >
                    No events scheduled this week.
                  </div>
                ) : (
                  weekEvents.map((e, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        gap: 14,
                        padding: '10px 22px',
                        borderBottom: i < weekEvents.length - 1 ? '1px solid var(--border)' : 'none'
                      }}
                    >
                      <div style={{ textAlign: 'right', minWidth: 48 }}>
                        <div
                          className="mono"
                          style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: 1 }}
                        >
                          {WEEKDAY_SHORT[e.date.getDay()]}
                        </div>
                        <div className="kpi-num" style={{ fontSize: 20, marginTop: 2 }}>
                          {e.date.getDate()}
                        </div>
                      </div>
                      <div style={{ flex: 1, minWidth: 0, paddingTop: 2 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--ink)' }}>
                          {e.title}
                        </div>
                        <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                          {e.time} · {e.location}
                          {e.attendees !== undefined && ` · ${e.attendees} attending`}
                        </div>
                      </div>
                      <span className={`chip ${e.type === 'Internal' ? 'muted' : 'info'}`}>
                        {e.type}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DashboardV3;
